// ============================================================================
// Consolidated Processors
// Used by dependent-field-processing.js and process-dynamic-content.js
// ============================================================================
import { FACE_SIMILARITY_THRESHOLD } from 'constants/idv';
import moment from 'moment';

import { getFullCountryName, getFullStateName } from '../countries';

// ============================================================================
// Name Processors
// ============================================================================

// Creates variations of a full name from its components
export const createFullNameVariations = (firstName, middleNames, lastName) => {
  const variations = [];

  // Full name with middle names
  if (firstName && lastName) {
    const fullName = [firstName, middleNames, lastName].filter(Boolean).join(' ');
    variations.push(fullName);

    // First + last name (if different from full name)
    const firstLast = `${firstName} ${lastName}`;
    if (firstLast !== fullName) {
      variations.push(firstLast);
    }
  }

  return variations;
};

// Format names for dropdown options
const formatForDropdown = names =>
  names.map(name => ({
    name,
    value: name,
  }));

// Get current legal names based on form values
const getCurrentNames = formValues => {
  if (!formValues) return [];

  // If single_name_only is true, only use single_name if it exists
  if (formValues.single_name_only) {
    return formValues.single_name ? [formValues.single_name] : [];
  }

  // Otherwise use first/middle/last name fields
  return createFullNameVariations(
    formValues.first_name,
    formValues.middle_names,
    formValues.last_name,
  );
};

// Get all other names (excluding current legal name)
const getOtherNames = formValues => {
  if (!formValues) return [];

  const nameArrays = {
    firstNames: [],
    firstMiddleNames: [],
    lastNames: [],
    fullNames: [],
  };

  const addUnique = (array, value) => {
    if (value && !array.includes(value)) array.push(value);
  };

  // Add current first name for combinations (only if not using single_name_only)
  if (formValues.first_name) {
    addUnique(nameArrays.firstNames, formValues.first_name);

    if (formValues.middle_names) {
      const firstMiddle = `${formValues.first_name} ${formValues.middle_names}`;
      addUnique(nameArrays.firstMiddleNames, firstMiddle);
    }
  }

  // Process other names array
  if (Array.isArray(formValues.other_names)) {
    formValues.other_names.forEach(otherName => {
      if (!otherName?.other_name_category) return;

      const category = otherName.other_name_category;

      switch (category) {
        case 'maiden':
        case 'previous_married_name':
          if (otherName.other_last_name) {
            addUnique(nameArrays.lastNames, otherName.other_last_name);
          }
          break;

        case 'legal_name_change':
        case 'alias':
          if (otherName.other_single_name) {
            addUnique(nameArrays.fullNames, otherName.other_single_name);
          } else {
            if (otherName.other_first_name) {
              addUnique(nameArrays.firstNames, otherName.other_first_name);

              if (otherName.other_middle_names) {
                const firstMiddle = `${otherName.other_first_name} ${otherName.other_middle_names}`;
                addUnique(nameArrays.firstMiddleNames, firstMiddle);
              }

              // Use createFullNameVariations for full name variations
              const variations = createFullNameVariations(
                otherName.other_first_name,
                otherName.other_middle_names,
                otherName.other_last_name,
              );
              variations.forEach(variation => addUnique(nameArrays.fullNames, variation));
            } else if (otherName.other_last_name) {
              addUnique(nameArrays.lastNames, otherName.other_last_name);
            }
          }
          break;
      }
    });
  }

  // Generate combinations with maiden/previous names
  nameArrays.lastNames.forEach(lastName => {
    nameArrays.firstNames.forEach(firstName => {
      addUnique(nameArrays.fullNames, `${firstName} ${lastName}`);
    });

    nameArrays.firstMiddleNames.forEach(firstMiddle => {
      addUnique(nameArrays.fullNames, `${firstMiddle} ${lastName}`);
    });
  });

  return nameArrays.fullNames;
};

// Get all names (current + other)
export const getAllNames = formValues => {
  if (!formValues) return [];
  return [...getCurrentNames(formValues), ...getOtherNames(formValues)];
};

// Get other names formatted for dropdown (used by processDynamicContent)
export const buildOtherNameVersions = formValues => {
  if (!formValues) return [];
  return formatForDropdown(getOtherNames(formValues));
};

// Template helper: Get full name (avoids CSP issues with new Function())
// Prefer single_name if it exists, otherwise construct from first_name + last_name
// Falls back to candidateFullName from processor_params if form fields not available
export const getFullName = (values, processorParams = {}) => {
  let fullName = '';

  // Prefer single_name if it exists
  if (values?.single_name?.trim()) {
    fullName = values.single_name.trim();
  } else {
    // Construct from first_name + last_name
    const firstName = values?.first_name?.trim() || '';
    const lastName = values?.last_name?.trim() || '';
    if (firstName || lastName) {
      fullName = `${firstName} ${lastName}`.trim();
    }
  }

  // Fallback to candidate full name from processor params (camelCased by frontend)
  return fullName || processorParams.candidateFullName || '';
};

// ============================================================================
// Location Processors
// ============================================================================

// Constructs role location from state and country (using full names)
export const getRoleLocationName = values => {
  if (!values) return '';
  // Construct location from available location fields
  const parts = [];

  // Get full state name if state code is provided
  if (values.role_location_state?.trim()) {
    const fullStateName = getFullStateName(
      values.role_location_state.trim(),
      values.role_location_country,
    );
    if (fullStateName) parts.push(fullStateName);
  }

  // Get full country name if country code is provided
  if (values.role_location_country?.trim()) {
    const fullCountryName = getFullCountryName(values.role_location_country.trim());
    if (fullCountryName) parts.push(fullCountryName);
  }

  return parts.join(', ') || '';
};

// ============================================================================
// Country Processors
// ============================================================================

const DATE_FORMAT = 'DD/MM/YYYY';
const parseDate = date => moment(date, DATE_FORMAT, true);

export const getUniqueCountries = formValues => {
  const countries = new Set();

  const residential = formValues?.residential_address?.country;
  if (residential) countries.add(residential);

  const previous = Array.isArray(formValues?.previous_addresses)
    ? formValues.previous_addresses
    : [];

  previous.forEach(entry => {
    const country = entry?.previous_address?.country;
    if (country) countries.add(country);
  });

  return Array.from(countries);
};

export const getCountriesToCheck = (formValues, config = {}) => {
  if (!formValues || config?.mode !== 'address_history') return [];

  const countryDurations = new Map();
  const now = moment();
  const checkPeriodYears = config.requiredAddressYears || 10;
  const cutoffDate = moment().subtract(checkPeriodYears, 'years');

  // Convert addresses into sorted date ranges with country info
  let dateRanges = [];

  // Add current address
  if (formValues.residential_address?.country && formValues.current_address_residence_from) {
    const fromDate = parseDate(formValues.current_address_residence_from);
    if (fromDate.isValid()) {
      dateRanges.push({
        country: formValues.residential_address.country,
        from: fromDate,
        to: now,
      });
    }
  }

  // Add previous addresses
  if (Array.isArray(formValues.previous_addresses)) {
    dateRanges = dateRanges.concat(
      formValues.previous_addresses
        .filter(address => {
          return (
            address?.previous_address?.country &&
            address?.previous_address_residence_from &&
            address?.previous_address_residence_to
          );
        })
        .map(address => ({
          country: address.previous_address.country,
          from: parseDate(address.previous_address_residence_from),
          to: parseDate(address.previous_address_residence_to),
        }))
        .filter(range => range.from.isValid() && range.to.isValid()),
    );
  }

  // Sort ranges by 'to' date, descending
  dateRanges.sort((a, b) => b.to.valueOf() - a.to.valueOf());

  // Process each range
  dateRanges.forEach(range => {
    // Check if any part of the range falls within check period
    if (range.from.isBefore(now) && range.to.isAfter(cutoffDate)) {
      const durationMonths = range.to.diff(range.from, 'months');

      if (durationMonths > 0) {
        countryDurations.set(
          range.country,
          (countryDurations.get(range.country) || 0) + durationMonths,
        );
      }
    }
  });

  // Filter countries by minimum residence duration
  const minMonths = config.minimumResidenceMonths || 6;
  const qualifyingCountries = Array.from(countryDurations.entries())
    .filter(([_, duration]) => duration >= minMonths)
    .map(([country]) => country);

  // Add mustIncludeCountries countries
  const mustIncludeCountries = Array.isArray(config.mustIncludeCountries)
    ? config.mustIncludeCountries.filter(Boolean)
    : [];

  // Combine and deduplicate countries
  const finalCountries = [...new Set([...mustIncludeCountries, ...qualifyingCountries])];

  // Apply cap if specified
  if (config.countriesCap) {
    if (mustIncludeCountries.length >= config.countriesCap) {
      return mustIncludeCountries;
    }

    const cappedFinal = finalCountries.slice(0, config.countriesCap);
    return cappedFinal;
  }
  return finalCountries;
};

// ============================================================================
// ID Verification Processors
// ============================================================================

export const getNationalityAlpha2 = values => {
  const nationalitySelection = values?.nationality_selection;
  const auNzCitizen = values?.au_nz_citizen;
  const passportNationality = values?.passport_nationality;

  if (nationalitySelection) return nationalitySelection;
  if (auNzCitizen === 'au_citizen') return 'AU';
  if (auNzCitizen === 'nz_citizen') return 'NZ';

  return passportNationality || undefined;
};

export const getNationalityFullName = values => {
  const alpha2 = getNationalityAlpha2(values);
  return alpha2 ? getFullCountryName(alpha2) : undefined;
};

// Old code workaround for Woolworths
export const getPassportOrIdNumber = values => {
  const passportNumber = values?.passport_number;
  return passportNumber || undefined;
};

export function getIsPhotoIdSelected(values, processorParams = {}) {
  // First check if we have actual verification data from ID uploads
  if (values?.hasPhotoId === true) {
    return true;
  }

  // Fallback to checking ID type if no verification data available
  const selectedIdTypes = values?.selectedIdTypes || {};
  const photoIdOptions = processorParams.photoIdOptions || [];

  if (photoIdOptions.length === 0) return false;

  return Object.keys(selectedIdTypes).some(t => photoIdOptions.includes(t));
}

export function getIsDobIdSelected(values, processorParams = {}) {
  // First check if we have actual verification data from ID uploads
  // includes_dob comes from Regula verification indicating the ID contains a DOB
  if (values?.hasDobId === true) {
    return true;
  }

  // Fallback to checking ID type if no verification data available
  const selectedIdTypes = values?.selectedIdTypes || {};
  const dobIdOptions = processorParams.dobIdOptions || [];

  if (dobIdOptions.length === 0) return false;

  return Object.keys(selectedIdTypes).some(t => dobIdOptions.includes(t));
}

export function getHasFaceSimilarityPassed(values) {
  const uploads = values?.validUploads || [];

  for (const u of uploads) {
    const faceSimilarity = u?.id_upload_detail?.face_similarity;

    // If face_similarity is null, this ID doesn't apply face similarity
    if (faceSimilarity === null) continue;

    // If we have a score and it meets the threshold, return true
    if (typeof faceSimilarity === 'number' && faceSimilarity >= FACE_SIMILARITY_THRESHOLD) {
      return true;
    }
  }

  return false;
}

// "True" if any upload in the set was authenticated by provider and has a photo
// Returns true if at least ONE upload meets the requirement
export function getHasIdVerifiedByProvider(values) {
  const uploads = values?.validUploads || [];

  for (const u of uploads) {
    const authenticationSource = u?.id_upload_detail?.authentication_source;
    const hasPhoto = u?.id_upload_detail?.has_photo;

    // Skip if not provider authenticated with photo
    if (authenticationSource !== 'provider' || !hasPhoto) continue;

    return true;
  }

  return false;
}

export function getIdValuesSelected(values) {
  const uploads = values?.validUploads || [];
  return uploads.map(u => u?.metadata?.id_type || null).filter(Boolean);
}

export function getIdTypesSelected(values) {
  const selectedIdTypes = values?.selectedIdTypes || {};
  return Object.keys(selectedIdTypes);
}

export function getIdRequirementsListsUsed(values) {
  const uploads = values?.validUploads || [];
  return uploads.map(u => u?.id_upload_detail?.id_requirements_list).filter(Boolean);
}
